import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { SurveyQuestion } from '../../fetchSurvey/types';
import { resolveSurveyQuestionPlaceholders } from '../../fetchSurvey/utils/resolveSurveyQuestionPlaceholders';
import type { LogicContext } from '../../surveyLogic/types';
import {
  getVisibleMcqOptions,
  getVisibleRankOrderOptions,
  getVisibleScaleColumns,
  getVisibleStatementRows,
} from '../../surveyLogic/visibility';

function arraysShallowEqual<T>(left: readonly T[], right: readonly T[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => item === right[index]);
}

function prepareRenderableQuestion(
  question: SurveyQuestion,
  context: LogicContext
): SurveyQuestion {
  const { answers, questionsById, customFieldValues } = context;

  const questionText = resolveSurveyQuestionPlaceholders(
    question.questionText,
    answers,
    questionsById,
    customFieldValues
  );

  const questionDescription =
    question.questionDescription !== undefined
      ? resolveSurveyQuestionPlaceholders(
          question.questionDescription,
          answers,
          questionsById,
          customFieldValues
        )
      : undefined;

  let next: SurveyQuestion = question;
  let changed = false;

  if (questionText !== question.questionText) {
    next = { ...next, questionText };
    changed = true;
  }

  if (questionDescription !== question.questionDescription) {
    next = { ...next, questionDescription };
    changed = true;
  }

  const base = changed ? next : question;

  switch (question.type) {
    case QUESTION_TYPE.MCQ: {
      const options = getVisibleMcqOptions(question, context);
      if (!arraysShallowEqual(options, question.options)) {
        next = { ...base, options } as SurveyQuestion;
        changed = true;
      }
      break;
    }
    case QUESTION_TYPE.RANK_ORDER: {
      const options = getVisibleRankOrderOptions(question, context);
      if (!arraysShallowEqual(options, question.options)) {
        next = { ...base, options } as SurveyQuestion;
        changed = true;
      }
      break;
    }
    case QUESTION_TYPE.CSAT_MATRIX:
    case QUESTION_TYPE.CFM_MATRIX:
    case QUESTION_TYPE.RATING_MATRIX: {
      const statementRows = getVisibleStatementRows(question, context);
      const scaleColumns = getVisibleScaleColumns(question, context);
      const rowsChanged = !arraysShallowEqual(statementRows, question.statementRows);
      const colsChanged = !arraysShallowEqual(scaleColumns, question.scaleColumns);

      if (rowsChanged || colsChanged) {
        next = { ...base, statementRows, scaleColumns } as SurveyQuestion;
        changed = true;
      }
      break;
    }
    case QUESTION_TYPE.SLIDER_MATRIX: {
      const statementRows = getVisibleStatementRows(question, context);
      if (!arraysShallowEqual(statementRows, question.statementRows)) {
        next = { ...base, statementRows } as SurveyQuestion;
        changed = true;
      }
      break;
    }
    default:
      break;
  }

  return changed ? next : question;
}

export function prepareRenderableQuestions(
  questions: SurveyQuestion[],
  logicContext: LogicContext
): SurveyQuestion[] {
  let arrayChanged = false;

  const prepared = questions.map(question => {
    const next = prepareRenderableQuestion(question, logicContext);
    if (next !== question) {
      arrayChanged = true;
    }
    return next;
  });

  return arrayChanged ? prepared : questions;
}
